import { useEffect, useId, useState } from "react";
import { SettingsHelp } from "./ProposalSettingsUI";
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  FormControl,
  FormLabel,
  Input,
  Option,
  Select,
  Stack,
  Textarea,
  Typography,
} from "@mui/joy";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "../../lib/api";
import { proposalSaveError } from "../../lib/proposalPresentationDraft";
import {
  applyProductToLines,
  appendTemplate,
  applyMeasurement,
  sellingQuantity,
  templateChanges,
  worksheetFields,
  worksheetQuantity,
  duplicateService,
  newLine,
  serviceKey,
  starterTypes,
  startingService,
  tradeNames,
  type Discount,
  type ServiceConfiguration,
  type ServiceLine,
  type ServiceScope,
  type ServiceTemplate,
  type Specification,
  type Unit,
} from "../../shared/proposal-services";

const columns = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "repeat(2,minmax(0,1fr))" },
  gap: 2,
};
export function ServiceField({
  label,
  value,
  onChange,
  type = "text",
  help,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  help?: string;
}) {
  const id = useId();
  return (
    <FormControl>
      <FormLabel htmlFor={id}>
        {label}
        {help && <SettingsHelp title={label}>{help}</SettingsHelp>}
      </FormLabel>
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        slotProps={{
          input: { id, min: type === "number" ? 0 : undefined, step: "any" },
        }}
      />
    </FormControl>
  );
}
export function DiscountEditor({
  value,
  onChange,
  label,
}: {
  value?: Discount;
  onChange: (value: Discount) => void;
  label: string;
}) {
  return (
    <FormControl>
      <FormLabel>{label}</FormLabel>
      <Stack direction="row" spacing={1}>
        <Select
          aria-label={`${label} type`}
          value={value?.type || "amount"}
          onChange={(_, type) =>
            onChange({ type: type || "amount", value: value?.value || "0" })
          }
        >
          <Option value="amount">$</Option>
          <Option value="percent">%</Option>
        </Select>
        <Input
          type="number"
          value={value?.value || "0"}
          onChange={(e) =>
            onChange({ type: value?.type || "amount", value: e.target.value })
          }
          slotProps={{ input: { min: 0, step: "any", "aria-label": label } }}
        />
      </Stack>
    </FormControl>
  );
}
export function Specifications({
  value,
  onChange,
}: {
  value: Specification[];
  onChange: (value: Specification[]) => void;
}) {
  return (
    <Box sx={columns}>
      {value.map((spec, index) => (
        <ServiceField
          key={index}
          label={spec.label}
          value={spec.value}
          onChange={(text) =>
            onChange(
              value.map((row, i) =>
                i === index ? { ...row, value: text } : row,
              ),
            )
          }
        />
      ))}
    </Box>
  );
}
function CatalogProduct({
  onChoose,
}: {
  onChoose: (item: { id: number; name: string; sku: string }) => void;
}) {
  const [input, setInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const result = useQuery({
    queryKey: ["service-catalog", search, page],
    enabled: !!search.trim(),
    queryFn: async () =>
      (
        await apiClient.get<{
          count: number;
          has_more: boolean;
          results: {
            id: number;
            name: string;
            sku: string;
            distributor: string;
            ordering_unit: string;
          }[];
        }>(
          `/api/proposal-service-templates/catalog/?search=${encodeURIComponent(search)}&page=${page}`,
        )
      ).data,
  });
  return (
    <Card variant="soft">
      <Typography level="title-sm">
        Choose an existing distributor product
      </Typography>
      <Typography level="body-xs">
        This copies the product reference. Enter your installed selling price
        separately; purchasing units are confirmed when ordering.
      </Typography>
      <Stack direction="row" gap={1}>
        <Input
          aria-label="Search distributor products"
          placeholder="Product name or SKU"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          sx={{ minWidth: 0, flex: 1 }}
        />
        <Button
          variant="outlined"
          onClick={() => {
            setSearch(input);
            setPage(1);
          }}
        >
          Search
        </Button>
      </Stack>
      {result.isError && (
        <Alert color="warning">
          Products could not load.{" "}
          <Button onClick={() => result.refetch()}>Retry</Button>
        </Alert>
      )}
      {result.data && (
        <>
          <Typography level="body-sm">
            {result.data.count} matching products · page {page}
          </Typography>
          {result.data.results.map((item) => (
            <Button
              key={item.id}
              variant="plain"
              sx={{ justifyContent: "start", textAlign: "left" }}
              onClick={() => onChoose(item)}
            >
              {item.name} · {item.sku} · {item.distributor} ·{" "}
              {item.ordering_unit}
            </Button>
          ))}
          <Stack direction="row" gap={1}>
            <Button disabled={page === 1} onClick={() => setPage(page - 1)}>
              Previous
            </Button>
            <Button
              disabled={!result.data.has_more}
              onClick={() => setPage(page + 1)}
            >
              Next products
            </Button>
          </Stack>
        </>
      )}
    </Card>
  );
}

export function LineEditor({
  line,
  lines = [],
  measurements = [],
  onChange,
  onDuplicate,
  onRemove,
  templateMode = false,
}: {
  templateMode?: boolean;
  line: ServiceLine;
  lines?: ServiceLine[];
  measurements?: NonNullable<ServiceLine["source"]>[];
  onChange: (line: ServiceLine) => void;
  onDuplicate: () => void;
  onRemove: () => void;
}) {
  const edit = (fields: Partial<ServiceLine>) =>
    onChange({
      ...line,
      ...fields,
      ...((fields.quantity !== undefined || fields.worksheet !== undefined) &&
      line.source &&
      !fields.source
        ? {
            source: {
              ...line.source,
              manually_overridden: true,
              confirmed: false,
            },
          }
        : {}),
    });
  return (
    <Card variant="outlined" sx={{ minWidth: 0 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
      >
        <Chip color={line.unit_price === null ? "warning" : "neutral"}>
          {line.unit_price === null
            ? "Needs pricing"
            : line.optional
              ? "Optional upgrade"
              : line.is_fee
                ? "Required charge"
                : "Included work"}
        </Chip>
        <Stack direction="row">
          <Button variant="plain" size="sm" onClick={onDuplicate}>
            Duplicate
          </Button>
          <Button variant="plain" size="sm" color="danger" onClick={onRemove}>
            Remove
          </Button>
        </Stack>
      </Stack>
      <ServiceField
        label="Work / item name"
        value={line.name}
        onChange={(name) => edit({ name })}
      />
      <Box
        sx={{
          ...columns,
          gridTemplateColumns: { xs: "1fr", sm: "repeat(3,minmax(0,1fr))" },
        }}
      >
        <ServiceField
          label={templateMode ? "Starting quantity" : "Selling quantity"}
          type="number"
          value={sellingQuantity(line, lines)}
          onChange={(quantity) =>
            edit({ quantity, worksheet: undefined, quantity_rule: undefined })
          }
          help={
            templateMode
              ? "This is a reusable starting quantity. Your team must confirm lengths, areas and counts on each proposal. Editing it switches from worksheet to manual quantity."
              : "The quantity used to price this work. Supplier ordering quantities and package rounding are reviewed separately."
          }
        />
        <FormControl>
          <FormLabel>Unit</FormLabel>
          <Select
            value={line.unit}
            onChange={(_, unit) => edit({ unit: unit || "item" })}
          >
            {(
              [
                "ea",
                "lf",
                "sf",
                "sq",
                "item",
                "opening",
                "run",
                "hour",
              ] as Unit[]
            ).map((unit) => (
              <Option key={unit} value={unit}>
                {unit === "sq"
                  ? "Square (100 sq ft)"
                  : unit === "sf"
                    ? "Square feet"
                    : unit === "lf"
                      ? "Linear feet"
                      : unit}
              </Option>
            ))}
          </Select>
        </FormControl>
        <ServiceField
          label={
            templateMode ? "Price per unit ($)" : "Installed price per unit ($)"
          }
          type="number"
          value={line.unit_price ?? ""}
          onChange={(value) => edit({ unit_price: value || null })}
          help="Customer selling price, including the work defined here. Blank means not priced; enter 0 only for an included no-charge item."
        />
      </Box>
      {templateMode && (
        <>
          <FormControl>
            <FormLabel>
              Customer choice{" "}
              <SettingsHelp title="Customer choice">
                Included work and required charges cannot be removed by the
                customer. Optional upgrades can be chosen or declined.
              </SettingsHelp>
            </FormLabel>
            <Select
              aria-label="Customer choice"
              value={
                line.optional ? "optional" : line.is_fee ? "fee" : "included"
              }
              onChange={(_, choice) =>
                edit({
                  optional: choice === "optional",
                  is_fee: choice === "fee",
                  selected: choice === "optional" ? line.selected : false,
                })
              }
            >
              <Option value="included">Included work — required</Option>
              <Option value="fee">Required charge / fee</Option>
              <Option value="optional">
                Optional upgrade — customer chooses
              </Option>
            </Select>
          </FormControl>
          <FormControl>
            <FormLabel>Included work / exclusions</FormLabel>
            <Textarea
              minRows={2}
              value={line.description}
              onChange={(e) => edit({ description: e.target.value })}
            />
          </FormControl>
          <Stack direction="row" gap={2} flexWrap="wrap">
            <Checkbox
              label="Taxable"
              checked={line.taxable}
              onChange={(e) => edit({ taxable: e.target.checked })}
            />
            <Checkbox
              label="Eligible for discounts"
              checked={line.discountable !== false}
              onChange={(e) => edit({ discountable: e.target.checked })}
            />
          </Stack>
        </>
      )}
      <Box
        component={templateMode ? "details" : "div"}
        sx={{
          "& > summary": { cursor: "pointer", py: 1.5 },
          "& > div": { minWidth: 0 },
        }}
      >
        {templateMode && (
          <summary>
            Advanced item settings — measurements, product details & discounts
          </summary>
        )}
        <Stack spacing={2}>
          {line.worksheet && (
            <Card variant="soft">
              <Typography level="title-sm">Measurement worksheet</Typography>
              <Typography level="body-xs">
                Quantities are calculated from these inputs. Editing Selling
                quantity switches back to a manual quantity.
              </Typography>
              <Box sx={columns}>
                {worksheetFields[line.worksheet.kind].map((field) =>
                  field.toggle ? (
                    <Checkbox
                      key={field.key}
                      label={field.label}
                      checked={Boolean(
                        line.worksheet?.[field.key] ??
                          field.key === "deduct_gates",
                      )}
                      onChange={(e) =>
                        edit({
                          worksheet: {
                            ...line.worksheet!,
                            [field.key]: e.target.checked,
                          },
                        })
                      }
                    />
                  ) : (
                    <ServiceField
                      key={field.key}
                      label={field.label}
                      type="number"
                      value={String(line.worksheet?.[field.key] ?? "")}
                      onChange={(value) =>
                        edit({
                          worksheet: {
                            ...line.worksheet!,
                            [field.key]: value || "0",
                          },
                        })
                      }
                    />
                  ),
                )}
              </Box>
              {line.worksheet.kind === "windows" && (
                <>
                  <FormControl>
                    <FormLabel>Installation method</FormLabel>
                    <Select
                      value={line.worksheet.installation || "insert"}
                      onChange={(_, installation) =>
                        edit({
                          worksheet: {
                            ...line.worksheet!,
                            installation: installation || "insert",
                            size_bands: [],
                            custom_price: true,
                          },
                          unit_price: null,
                        })
                      }
                    >
                      <Option value="insert">Insert replacement</Option>
                      <Option value="full_frame">Full-frame replacement</Option>
                    </Select>
                  </FormControl>
                  <Typography level="body-xs">
                    Changing installation method clears pricing so the correct
                    scope and rate can be reviewed.
                  </Typography>
                  <details>
                    <summary>Window size-band rates</summary>
                    {line.worksheet.size_bands?.map((band, i) => (
                      <Box sx={columns} key={i}>
                        {(
                          ["max_width", "max_height", "unit_price"] as const
                        ).map((key) => (
                          <ServiceField
                            key={key}
                            label={
                              key === "max_width"
                                ? "Maximum width (in)"
                                : key === "max_height"
                                  ? "Maximum height (in)"
                                  : "Installed price ($)"
                            }
                            type="number"
                            value={band[key]}
                            onChange={(value) =>
                              edit({
                                worksheet: {
                                  ...line.worksheet!,
                                  size_bands: line.worksheet!.size_bands!.map(
                                    (b, j) =>
                                      i === j ? { ...b, [key]: value } : b,
                                  ),
                                },
                              })
                            }
                          />
                        ))}
                        <Button
                          variant="plain"
                          onClick={() =>
                            edit({
                              worksheet: {
                                ...line.worksheet!,
                                size_bands: line.worksheet!.size_bands!.filter(
                                  (_, j) => j !== i,
                                ),
                              },
                            })
                          }
                        >
                          Remove size band
                        </Button>
                      </Box>
                    ))}
                    <Button
                      variant="outlined"
                      onClick={() =>
                        edit({
                          worksheet: {
                            ...line.worksheet!,
                            size_bands: [
                              ...(line.worksheet!.size_bands || []),
                              {
                                max_width: "36",
                                max_height: "60",
                                unit_price: "0",
                              },
                            ],
                          },
                        })
                      }
                    >
                      Add size band
                    </Button>
                  </details>
                </>
              )}
            </Card>
          )}
          {lines.length > 1 && (
            <Card variant="soft">
              <Typography level="title-sm">
                Quantity from another measured row
              </Typography>
              <Typography level="body-xs">
                Use a configured accessory or assembly rule. Review exceptions
                such as corners, gate posts and shared ends separately.
              </Typography>
              <Select
                aria-label={`Quantity source for ${line.name}`}
                value={line.quantity_rule?.source_line || ""}
                onChange={(_, key) =>
                  edit({
                    worksheet: undefined,
                    quantity_rule: key
                      ? {
                          source_line: key,
                          per_quantity: "1",
                          multiplier: "1",
                          extra: "0",
                          round_up: true,
                        }
                      : undefined,
                  })
                }
              >
                <Option value="">Manual / worksheet quantity</Option>
                {lines
                  .filter(
                    (row) =>
                      row.key !== line.key &&
                      !row.optional &&
                      !row.quantity_rule,
                  )
                  .map((row) => (
                    <Option key={row.key} value={row.key}>
                      {row.name} ({worksheetQuantity(row)} {row.unit})
                    </Option>
                  ))}
              </Select>
              {line.quantity_rule && (
                <Box sx={columns}>
                  <ServiceField
                    label="For every source quantity"
                    type="number"
                    value={line.quantity_rule.per_quantity}
                    onChange={(per_quantity) =>
                      edit({
                        quantity_rule: { ...line.quantity_rule!, per_quantity },
                      })
                    }
                  />
                  <ServiceField
                    label="Units needed each time"
                    type="number"
                    value={line.quantity_rule.multiplier}
                    onChange={(multiplier) =>
                      edit({
                        quantity_rule: { ...line.quantity_rule!, multiplier },
                      })
                    }
                  />
                  <ServiceField
                    label="Additional units"
                    type="number"
                    value={line.quantity_rule.extra}
                    onChange={(extra) =>
                      edit({ quantity_rule: { ...line.quantity_rule!, extra } })
                    }
                  />
                  <Checkbox
                    label="Round source groups up to whole groups"
                    checked={line.quantity_rule.round_up}
                    onChange={(e) =>
                      edit({
                        quantity_rule: {
                          ...line.quantity_rule!,
                          round_up: e.target.checked,
                        },
                      })
                    }
                  />
                </Box>
              )}
            </Card>
          )}
          <Specifications
            value={line.specifications}
            onChange={(specifications) => edit({ specifications })}
          />
          {!templateMode && (
            <>
              <FormControl>
                <FormLabel>Included work / exclusions</FormLabel>
                <Textarea
                  minRows={2}
                  value={line.description}
                  onChange={(e) => edit({ description: e.target.value })}
                />
              </FormControl>
              <Stack direction="row" gap={2} flexWrap="wrap">
                <Checkbox
                  label="Customer may decline this item"
                  checked={line.optional}
                  onChange={(e) =>
                    edit({
                      optional: e.target.checked,
                      is_fee: e.target.checked ? false : line.is_fee,
                    })
                  }
                />
                <Checkbox
                  label="Required charge"
                  checked={line.is_fee}
                  onChange={(e) =>
                    edit({
                      is_fee: e.target.checked,
                      optional: e.target.checked ? false : line.optional,
                    })
                  }
                />
                <Checkbox
                  label="Eligible for discounts"
                  checked={line.discountable !== false}
                  onChange={(e) => edit({ discountable: e.target.checked })}
                />
                <Checkbox
                  label="Taxable"
                  checked={line.taxable}
                  onChange={(e) => edit({ taxable: e.target.checked })}
                />
              </Stack>
            </>
          )}
          {line.optional && (
            <Checkbox
              label="Selected by default"
              checked={line.selected}
              onChange={(e) => edit({ selected: e.target.checked })}
            />
          )}
          {line.worksheet?.kind === "gutters" && measurements.length > 0 && (
            <Card variant="soft">
              <Typography level="title-sm">
                Use measured eaves as a starting point
              </Typography>
              <Typography level="body-xs">
                Choose one report, then confirm which edges receive gutters.
                Applying a source replaces this run’s length; reports are never
                added together.
              </Typography>
              {measurements.map((source) => (
                <Button
                  key={source.record_id}
                  size="sm"
                  variant="outlined"
                  onClick={() => onChange(applyMeasurement(line, source))}
                >
                  {source.label}: {line.quantity} → {source.original_quantity}{" "}
                  lf
                  {line.source?.record_id === source.record_id &&
                  line.source?.revision !== source.revision
                    ? " · Newer measurement available"
                    : ""}
                </Button>
              ))}
            </Card>
          )}
          <details>
            <summary>Product, discount & measurement source</summary>
            <CatalogProduct
              onChoose={(item) =>
                edit({
                  product_name: item.name,
                  sku: item.sku,
                  catalog_item: item.id,
                })
              }
            />
            <ServiceField
              label="Internal cost per selling unit (optional)"
              type="number"
              value={line.internal_unit_cost || ""}
              help="For your team's costing reference only. Never shown to customers. This does not change the selling price."
              onChange={(internal_unit_cost) =>
                edit({ internal_unit_cost: internal_unit_cost || null })
              }
            />
            <Stack spacing={2} sx={{ pt: 2 }}>
              <Box sx={columns}>
                <ServiceField
                  label="Product / series"
                  value={line.product_name}
                  onChange={(product_name) => edit({ product_name })}
                />
                <ServiceField
                  label="Manufacturer"
                  value={line.manufacturer}
                  onChange={(manufacturer) => edit({ manufacturer })}
                />
                <ServiceField
                  label="SKU (if known)"
                  value={line.sku}
                  onChange={(sku) => edit({ sku })}
                />
                <ServiceField
                  label="Minimum installed unit price ($, optional)"
                  type="number"
                  value={line.minimum_unit_price || ""}
                  onChange={(value) =>
                    edit({ minimum_unit_price: value || null })
                  }
                  help="Template minimums are enforced for salespeople after discounts. Org Admin overrides are recorded in edit history."
                />
                <DiscountEditor
                  label="Line discount"
                  value={line.discount}
                  onChange={(discount) => edit({ discount })}
                />
              </Box>
              <ServiceField
                label="Quantity source (optional)"
                value={line.source?.label || ""}
                onChange={(label) =>
                  edit({
                    source: label
                      ? {
                          label,
                          unit: line.unit,
                          original_quantity: line.quantity,
                          manually_overridden: false,
                          confirmed: false,
                        }
                      : undefined,
                  })
                }
                help="Record the report, field measurement, or worksheet used. Confirm it before sending; do not substitute roof area for siding area."
              />
              {line.source && (
                <Checkbox
                  label="I verified this quantity and unit"
                  checked={line.source.confirmed}
                  onChange={(e) =>
                    edit({
                      source: { ...line.source!, confirmed: e.target.checked },
                    })
                  }
                />
              )}
              <Checkbox
                label="Final product / dimensions need verification before ordering"
                checked={line.requires_order_verification}
                onChange={(e) =>
                  edit({ requires_order_verification: e.target.checked })
                }
              />
            </Stack>
          </details>{" "}
        </Stack>
      </Box>
    </Card>
  );
}

export function ServiceEditor({
  value,
  onChange,
  showLibrary = true,
  proposalId,
}: {
  value?: ServiceConfiguration;
  onChange: (value: ServiceConfiguration) => void;
  showLibrary?: boolean;
  proposalId?: number;
}) {
  const config =
    value?.version === 2
      ? value
      : { version: 2 as const, include_roofing: true, services: [] };
  const [active, setActive] = useState<string>("");
  const [search, setSearch] = useState("");
  const [selectedLines, setSelectedLines] = useState<Record<string, string[]>>(
    {},
  );
  const [templateReview, setTemplateReview] = useState<{
    service: ServiceScope;
    version: number;
  } | null>(null);
  const [folder, setFolder] = useState("");
  const [recent, setRecent] = useState<number[]>(() => {
    try {
      return JSON.parse(
        localStorage.getItem("proposal-service-recents") || "[]",
      );
    } catch {
      return [];
    }
  });
  const library = useQuery({
    queryKey: ["proposal-service-templates"],
    enabled: showLibrary,
    queryFn: async () => {
      const result = (await apiClient.get("/api/proposal-service-templates/"))
        .data as ServiceTemplate[] | { results: ServiceTemplate[] };
      return Array.isArray(result) ? result : result.results;
    },
  });
  const measurements = useQuery({
    queryKey: ["service-measurements", proposalId],
    enabled: !!proposalId,
    queryFn: async () =>
      (
        await apiClient.get<NonNullable<ServiceLine["source"]>[]>(
          `/api/proposals/${proposalId}/service-measurements/`,
        )
      ).data,
  });
  const repairTasks = useQuery({
    queryKey: ["proposal-repair-task-services"],
    enabled: showLibrary,
    queryFn: async () =>
      (
        await apiClient.get<ServiceScope[]>(
          "/api/proposal-service-templates/repair-tasks/",
        )
      ).data,
  });
  const services = config.services;
  const [pricedConfiguration, setPricedConfiguration] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setPricedConfiguration(value), 250);
    return () => clearTimeout(timer);
  }, [value]);
  const quote = useQuery({
    queryKey: ["service-preview", proposalId, pricedConfiguration],
    enabled: !!proposalId && !!pricedConfiguration?.version,
    retry: false,
    queryFn: async () =>
      (
        await apiClient.post(`/api/proposals/${proposalId}/service-preview/`, {
          configuration: pricedConfiguration,
        })
      ).data as import("../../shared/proposal-services").ServiceQuote,
  });
  const unpriced = services.flatMap((s) =>
    s.options.flatMap((o) =>
      o.lines
        .filter((l) => l.unit_price === null)
        .map((l) => `${s.name}: ${l.name}`),
    ),
  );
  const service = services.find((s) => s.key === active) || services[0];
  const change = (next: ServiceScope) =>
    onChange({
      ...config,
      services: services.map((s) => (s.key === next.key ? next : s)),
    });
  const add = (rows: ServiceScope[]) => {
    onChange({ ...config, services: [...services, ...rows] });
    setActive(rows[0].key);
  };
  const templates = (library.data || [])
    .filter(
      (t) =>
        !t.is_archived &&
        t.configuration &&
        (!folder || t.folder === folder) &&
        `${t.name} ${t.folder}`.toLowerCase().includes(search.toLowerCase()),
    )
    .sort(
      (a, b) =>
        Number(b.is_favorite) - Number(a.is_favorite) ||
        (recent.includes(a.id) ? recent.indexOf(a.id) : Infinity) -
          (recent.includes(b.id) ? recent.indexOf(b.id) : Infinity),
    );
  return (
    <Stack spacing={2}>
      <Stack
        direction="row"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
      >
        <Box>
          <Typography level="h3">Project services</Typography>
          <Typography level="body-sm">
            Choose the work, confirm quantities and prices, then send one
            proposal.
          </Typography>
        </Box>
        <Checkbox
          label="Include roofing packages"
          checked={config.include_roofing}
          onChange={(e) =>
            onChange({ ...config, include_roofing: e.target.checked })
          }
        />
      </Stack>
      <Card variant="soft">
        <Typography level="title-md">Add a service</Typography>
        <Select
          aria-label="Choose starter service"
          placeholder="Choose a starter…"
          value={null}
          onChange={(_, value: string | null) => {
            const starter = starterTypes[Number(value)];
            if (starter) add([startingService(starter.trade, starter.variant)]);
          }}
        >
          {starterTypes.map((starter, index) => (
            <Option value={String(index)} key={index}>
              {tradeNames[starter.trade]} · {starter.variant}
            </Option>
          ))}
        </Select>
        <Typography level="body-xs">
          Starters have no prices. Configure rates and scope here, or use your
          organization's saved templates.
        </Typography>
        {showLibrary && (
          <>
            <Box sx={columns}>
              <Input
                placeholder="Search templates & bundles"
                aria-label="Search service templates"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <Select
                aria-label="Template folder"
                value={folder}
                onChange={(_, value) => setFolder(value || "")}
              >
                <Option value="">All folders</Option>
                {[
                  ...new Set(
                    (library.data || []).map((t) => t.folder).filter(Boolean),
                  ),
                ].map((f) => (
                  <Option value={f} key={f}>
                    {f}
                  </Option>
                ))}
              </Select>
            </Box>
            {library.isError && (
              <Alert color="warning">
                Templates could not load.{" "}
                <Button onClick={() => library.refetch()}>Retry</Button>
              </Alert>
            )}
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {templates.map((t) => (
                <Button
                  variant="outlined"
                  size="sm"
                  key={t.id}
                  onClick={() => {
                    const combined = appendTemplate(config, t);
                    onChange(combined);
                    setActive(
                      combined.services[services.length]?.key || active,
                    );
                    const next = [
                      t.id,
                      ...recent.filter((id) => id !== t.id),
                    ].slice(0, 10);
                    setRecent(next);
                    try {
                      localStorage.setItem(
                        "proposal-service-recents",
                        JSON.stringify(next),
                      );
                    } catch {
                      /* optional convenience */
                    }
                  }}
                >
                  {t.is_favorite ? "★ " : ""}
                  {t.name}
                </Button>
              ))}
            </Stack>
          </>
        )}
      </Card>
      {showLibrary && !!repairTasks.data?.length && (
        <Card variant="soft">
          <Typography level="title-md">Existing repair task library</Typography>
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {repairTasks.data
              .filter((task) =>
                task.name.toLowerCase().includes(search.toLowerCase()),
              )
              .map((task) => (
                <Button
                  variant="outlined"
                  key={task.key}
                  onClick={() => add([duplicateService(task)])}
                >
                  {task.name}
                </Button>
              ))}
          </Stack>
        </Card>
      )}
      <Box sx={{ pt: 1 }}>
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography level="h3">Included Services</Typography>
          <Chip size="sm" variant="soft">{services.length} {services.length === 1 ? "service" : "services"}</Chip>
        </Stack>
        <Typography level="body-sm" sx={{ mt: 0.5, color: "text.secondary" }}>
          {services.length
            ? "These services have been added to this proposal. Select one to edit its scope, quantities and prices."
            : "No services added yet. Choose a starter or saved template above to add work to this proposal."}
        </Typography>
      </Box>
      {services.length > 0 && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "190px minmax(0,1fr)" },
            alignItems: "start",
            gap: 2,
          }}
        >
          <Stack gap={1} component="nav" aria-label="Included services">
            {services.map((s) => (
              <Button
                key={s.key}
                variant={s.key === service.key ? "solid" : "soft"}
                onClick={() => setActive(s.key)}
                sx={{ justifyContent: "flex-start", textAlign: "left" }}
              >
                {s.name}
              </Button>
            ))}
          </Stack>
          <Stack spacing={2}>
            <Card>
              <Stack
                direction="row"
                justifyContent="space-between"
                flexWrap="wrap"
                gap={1}
              >
                <Typography level="h4">{service.name}</Typography>
                <Stack direction="row">
                  <Button
                    variant="plain"
                    onClick={() => add([duplicateService(service)])}
                  >
                    Duplicate service
                  </Button>
                  <Button
                    color="danger"
                    variant="plain"
                    onClick={() =>
                      onChange({
                        ...config,
                        services: services.filter((s) => s.key !== service.key),
                      })
                    }
                  >
                    Remove service
                  </Button>
                </Stack>
              </Stack>
              {templates
                .filter(
                  (t) =>
                    service.template_version &&
                    t.version_ids?.includes(service.template_version) &&
                    t.version_id !== service.template_version,
                )
                .map((t) => {
                  const updated = t.configuration?.services.find((s) =>
                    s.options.some((o) =>
                      service.options.some((old) => old.key === o.key),
                    ),
                  );
                  return (
                    updated && (
                      <Alert
                        key={t.id}
                        color="warning"
                        endDecorator={
                          <Button
                            onClick={() =>
                              setTemplateReview({
                                service: {
                                  ...updated,
                                  key: service.key,
                                  template_version: t.version_id,
                                },
                                version: t.current_version,
                              })
                            }
                          >
                            Review template update
                          </Button>
                        }
                      >
                        Version {t.current_version} of {t.name} is available.
                        This draft still uses its saved version.
                      </Alert>
                    )
                  );
                })}
              {templateReview && templateReview.service.key === service.key && (
                <Card variant="outlined">
                  <Typography level="title-lg">
                    Review updated template · version {templateReview.version}
                  </Typography>
                  <Typography>
                    Applying replaces this service’s quantities, scope,
                    products, prices and terms with the current template.
                    Recheck property-specific measurements afterward.
                  </Typography>
                  {templateChanges(service, templateReview.service).map(
                    (text, index) => (
                      <Typography level="body-sm" key={index}>
                        {text}
                      </Typography>
                    ),
                  )}
                  <Stack direction="row" gap={1}>
                    <Button
                      onClick={() => {
                        change(templateReview.service);
                        setTemplateReview(null);
                      }}
                    >
                      Apply reviewed template
                    </Button>
                    <Button
                      variant="plain"
                      onClick={() => setTemplateReview(null)}
                    >
                      Keep saved version
                    </Button>
                  </Stack>
                </Card>
              )}
              <Box sx={columns}>
                <ServiceField
                  label="Service name"
                  value={service.name}
                  onChange={(name) => change({ ...service, name })}
                />
                <ServiceField
                  label="Area / location"
                  value={service.location}
                  onChange={(location) => change({ ...service, location })}
                />
              </Box>
              <Specifications
                value={service.specifications}
                onChange={(specifications) =>
                  change({ ...service, specifications })
                }
              />
              <Checkbox
                label="Customer may decline this entire service"
                checked={service.optional}
                onChange={(e) =>
                  change({ ...service, optional: e.target.checked })
                }
              />
              <ServiceField
                label="Alternative group (optional)"
                value={service.exclusive_group || ""}
                onChange={(exclusive_group) =>
                  change({ ...service, exclusive_group })
                }
                help="Give repair and replacement for the same area the same short group ID, such as front-wall. The customer can choose only one service in that group."
              />
            </Card>
            {service.options.map((option, oi) => (
              <Card key={option.key} variant="soft">
                <Stack
                  direction="row"
                  gap={1}
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography level="title-lg">
                    {service.options.length === 1
                      ? "Scope & price"
                      : `Package ${oi + 1}`}
                  </Typography>
                  {service.options.length > 1 && (
                    <Button
                      color="danger"
                      variant="plain"
                      onClick={() =>
                        change({
                          ...service,
                          options: service.options.filter(
                            (o) => o.key !== option.key,
                          ),
                          selected_option:
                            service.selected_option === option.key
                              ? service.options.find(
                                  (o) => o.key !== option.key,
                                )!.key
                              : service.selected_option,
                        })
                      }
                    >
                      Remove package
                    </Button>
                  )}
                </Stack>
                <ServiceField
                  label="Package name"
                  value={option.name}
                  onChange={(name) =>
                    change({
                      ...service,
                      options: service.options.map((o) =>
                        o.key === option.key ? { ...o, name } : o,
                      ),
                    })
                  }
                />
                <Typography level="body-xs">
                  Select rows to copy a configured product and price across
                  matching units. Measurements and locations stay on their
                  original rows.
                </Typography>
                <Button
                  variant="outlined"
                  disabled={(selectedLines[option.key]?.length || 0) < 2}
                  onClick={() =>
                    change({
                      ...service,
                      options: service.options.map((o) =>
                        o.key === option.key
                          ? {
                              ...o,
                              lines: applyProductToLines(
                                o.lines,
                                selectedLines[option.key][0],
                                selectedLines[option.key],
                              ),
                            }
                          : o,
                      ),
                    })
                  }
                >
                  Apply first selected product & rate to selected rows
                </Button>
                {option.lines.map((line) => (
                  <details key={line.key}>
                    <summary style={{ padding: 12, cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        aria-label={`Select ${line.name} for bulk update`}
                        checked={
                          selectedLines[option.key]?.includes(line.key) || false
                        }
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) =>
                          setSelectedLines((previous) => ({
                            ...previous,
                            [option.key]: e.target.checked
                              ? [...(previous[option.key] || []), line.key]
                              : (previous[option.key] || []).filter(
                                  (key) => key !== line.key,
                                ),
                          }))
                        }
                      />{" "}
                      {line.name} · {sellingQuantity(line, option.lines)}{" "}
                      {line.unit} ·{" "}
                      {line.unit_price === null
                        ? "Needs pricing"
                        : `$${line.unit_price} per unit`}{" "}
                      — Edit details
                    </summary>
                    <LineEditor
                      lines={option.lines}
                      measurements={measurements.data || []}
                      line={line}
                      onChange={(next) =>
                        change({
                          ...service,
                          options: service.options.map((o) =>
                            o.key === option.key
                              ? {
                                  ...o,
                                  lines: o.lines.map((l) =>
                                    l.key === line.key ? next : l,
                                  ),
                                }
                              : o,
                          ),
                        })
                      }
                      onDuplicate={() =>
                        change({
                          ...service,
                          options: service.options.map((o) =>
                            o.key === option.key
                              ? {
                                  ...o,
                                  lines: [
                                    ...o.lines,
                                    {
                                      ...structuredClone(line),
                                      key: serviceKey(),
                                    },
                                  ],
                                }
                              : o,
                          ),
                        })
                      }
                      onRemove={() =>
                        change({
                          ...service,
                          options: service.options.map((o) =>
                            o.key === option.key
                              ? {
                                  ...o,
                                  lines: o.lines.filter(
                                    (l) => l.key !== line.key,
                                  ),
                                }
                              : o,
                          ),
                        })
                      }
                    />
                  </details>
                ))}
                <Button
                  variant="outlined"
                  onClick={() =>
                    change({
                      ...service,
                      options: service.options.map((o) =>
                        o.key === option.key
                          ? { ...o, lines: [...o.lines, newLine()] }
                          : o,
                      ),
                    })
                  }
                >
                  Add item / opening / run / fee
                </Button>
                <DiscountEditor
                  label="Package discount"
                  value={option.discount}
                  onChange={(discount) =>
                    change({
                      ...service,
                      options: service.options.map((o) =>
                        o.key === option.key ? { ...o, discount } : o,
                      ),
                    })
                  }
                />
              </Card>
            ))}
            <Button
              variant="outlined"
              onClick={() => {
                const copy = structuredClone(service.options[0]);
                copy.key = serviceKey();
                copy.name = "Alternative package";
                change({ ...service, options: [...service.options, copy] });
              }}
            >
              Add alternative package
            </Button>
            <Box sx={columns}>
              <FormControl>
                <FormLabel>Service terms / exclusions (required before sending)</FormLabel>
                <Textarea
                  minRows={3}
                  value={service.terms}
                  onChange={(e) =>
                    change({ ...service, terms: e.target.value })
                  }
                />
              </FormControl>
              <FormControl>
                <FormLabel>Service warranty</FormLabel>
                <Textarea
                  minRows={3}
                  value={service.warranty}
                  onChange={(e) =>
                    change({ ...service, warranty: e.target.value })
                  }
                />
              </FormControl>
            </Box>
          </Stack>
        </Box>
      )}
      {services.length > 0 && (
        <Card>
          <Typography level="title-lg">Shared project charges</Typography>
          <Typography level="body-sm">
            Charged once for the whole project, even when a service is removed.
            Use this for mobilization or shared access; keep service-specific
            fees inside their service.
          </Typography>
          {config.project_charges?.map((line) => (
            <LineEditor
              key={line.key}
              line={line}
              onChange={(next) =>
                onChange({
                  ...config,
                  project_charges: config.project_charges?.map((row) =>
                    row.key === line.key
                      ? { ...next, optional: false, is_fee: true }
                      : row,
                  ),
                })
              }
              onDuplicate={() =>
                onChange({
                  ...config,
                  project_charges: [
                    ...(config.project_charges || []),
                    { ...structuredClone(line), key: serviceKey() },
                  ],
                })
              }
              onRemove={() =>
                onChange({
                  ...config,
                  project_charges: config.project_charges?.filter(
                    (row) => row.key !== line.key,
                  ),
                })
              }
            />
          ))}
          <Button
            variant="outlined"
            onClick={() =>
              onChange({
                ...config,
                project_charges: [
                  ...(config.project_charges || []),
                  {
                    ...newLine("Shared project charge"),
                    is_fee: true,
                    discountable: false,
                  },
                ],
              })
            }
          >
            Add shared required charge
          </Button>
        </Card>
      )}
      {services.length > 0 && (
        <Card>
          <DiscountEditor
            label="Project discount (allocated across selected work)"
            value={config.discount}
            onChange={(discount) => onChange({ ...config, discount })}
          />
          <Typography level="body-sm">
            Line discounts apply first, then package discounts, then the project
            discount. Tax applies to the discounted taxable amounts.
          </Typography>
        </Card>
      )}
      {proposalId && services.length > 0 && (
        <Card
          sx={{ position: "sticky", bottom: 8, zIndex: 2, boxShadow: "md" }}
        >
          <Typography level="title-lg">
            {unpriced.length
              ? `${unpriced.length} items need pricing`
              : "Project total · starting selections"}
          </Typography>
          {unpriced.length > 0 ? (
            <Typography color="warning">
              {unpriced.slice(0, 3).join(" · ")}
              {unpriced.length > 3 ? "…" : ""}
            </Typography>
          ) : quote.data &&
            pricedConfiguration === value &&
            !quote.isFetching ? (
            <Typography level="h3">
              {Number(quote.data.total).toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
              })}
            </Typography>
          ) : (
            <Typography role="status">
              {quote.isError
                ? proposalSaveError(quote.error)
                : "Updating total…"}
            </Typography>
          )}
          <Typography level="body-sm">
            {config.include_roofing
              ? "Includes the first roofing package and required charges. "
              : ""}
            Customers review their chosen package and upgrades before signing.
          </Typography>
        </Card>
      )}
    </Stack>
  );
}
